import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Save, Loader2, Check, Euro, Users, Zap, Crown, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const planIcons = {
  free: <Users className="w-5 h-5" />,
  starter: <Zap className="w-5 h-5" />,
  pro: <Crown className="w-5 h-5" />,
  business: <CreditCard className="w-5 h-5" />
};

const planColors = {
  free: 'border-gray-200 dark:border-gray-700',
  starter: 'border-blue-200 dark:border-blue-700',
  pro: 'border-emerald-200 dark:border-emerald-700',
  business: 'border-purple-200 dark:border-purple-700'
};

const planBgColors = {
  free: 'bg-gray-50 dark:bg-gray-900',
  starter: 'bg-blue-50 dark:bg-blue-950/20',
  pro: 'bg-emerald-50 dark:bg-emerald-950/20',
  business: 'bg-purple-50 dark:bg-purple-950/20'
};

export default function SubscriptionPricesPanel({ getHeaders }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [editedPrices, setEditedPrices] = useState({});
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchPlans();
    fetchStats();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/subscriptions/plans`);
      setPlans(response.data);
      // Initialize edited prices
      const prices = {};
      response.data.forEach(plan => {
        prices[plan.id] = plan.price;
      });
      setEditedPrices(prices);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Error cargando planes');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/subscriptions/admin/stats`, {
        headers: getHeaders()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handlePriceChange = (planId, newPrice) => {
    setEditedPrices(prev => ({
      ...prev,
      [planId]: parseFloat(newPrice) || 0
    }));
  };

  const handleSavePrice = async (planId) => {
    setSaving(prev => ({ ...prev, [planId]: true }));
    try {
      await axios.put(
        `${API_URL}/api/subscriptions/admin/plans/${planId}`,
        { price: editedPrices[planId] },
        { headers: getHeaders() }
      );
      toast.success('Precio actualizado');
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error actualizando precio');
    } finally {
      setSaving(prev => ({ ...prev, [planId]: false }));
    }
  };

  const hasChanges = (planId) => {
    const plan = plans.find(p => p.id === planId);
    return plan && editedPrices[planId] !== plan.price;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="subscription-prices-panel">
      {/* Stats Summary */}
      {stats && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-4"
        >
          <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-xl p-4 border border-emerald-200 dark:border-emerald-800">
            <p className="text-sm text-emerald-600 dark:text-emerald-400 mb-1">Negocios Suscritos</p>
            <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{stats.total_businesses || 0}</p>
          </div>
          <div className="bg-blue-50 dark:bg-blue-950/30 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">MRR (Ingresos Mensuales)</p>
            <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{(stats.mrr || 0).toFixed(2)}€</p>
          </div>
          <div className="bg-purple-50 dark:bg-purple-950/30 rounded-xl p-4 border border-purple-200 dark:border-purple-800">
            <p className="text-sm text-purple-600 dark:text-purple-400 mb-1">ARR (Ingresos Anuales)</p>
            <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{(stats.arr || 0).toFixed(2)}€</p>
          </div>
          <div className="bg-amber-50 dark:bg-amber-950/30 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
            <p className="text-sm text-amber-600 dark:text-amber-400 mb-1">Planes Activos</p>
            <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{Object.keys(stats.by_plan || {}).length}</p>
          </div>
        </motion.div>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {plans.map((plan, idx) => (
          <motion.div
            key={plan.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className={`rounded-2xl border-2 p-6 ${planColors[plan.id]} ${planBgColors[plan.id]}`}
            data-testid={`plan-card-${plan.id}`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  plan.id === 'free' ? 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300' :
                  plan.id === 'starter' ? 'bg-blue-200 dark:bg-blue-800 text-blue-600 dark:text-blue-300' :
                  plan.id === 'pro' ? 'bg-emerald-200 dark:bg-emerald-800 text-emerald-600 dark:text-emerald-300' :
                  'bg-purple-200 dark:bg-purple-800 text-purple-600 dark:text-purple-300'
                }`}>
                  {planIcons[plan.id]}
                </div>
                <div>
                  <h3 className="font-bold text-lg">{plan.name}</h3>
                  <p className="text-xs text-muted-foreground">
                    {plan.kupons_per_month === -1 ? 'Kupones ilimitados' : `${plan.kupons_per_month} kupones/mes`}
                  </p>
                </div>
              </div>
              
              {stats?.by_plan?.[plan.id] && (
                <span className="text-xs bg-white/50 dark:bg-black/20 px-2 py-1 rounded-full">
                  {stats.by_plan[plan.id]} negocios
                </span>
              )}
            </div>

            {/* Price Editor */}
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="text-xs text-muted-foreground mb-1 block">
                  Precio mensual
                </label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editedPrices[plan.id] || 0}
                    onChange={(e) => handlePriceChange(plan.id, e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-border bg-white dark:bg-gray-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    data-testid={`price-input-${plan.id}`}
                  />
                </div>
              </div>
              
              <button
                onClick={() => handleSavePrice(plan.id)}
                disabled={saving[plan.id] || !hasChanges(plan.id)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  hasChanges(plan.id)
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                }`}
                data-testid={`save-price-${plan.id}`}
              >
                {saving[plan.id] ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : hasChanges(plan.id) ? (
                  <Save className="w-4 h-4" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                {hasChanges(plan.id) ? 'Guardar' : 'Guardado'}
              </button>
            </div>

            {/* Features list */}
            <div className="mt-4 pt-4 border-t border-black/10 dark:border-white/10">
              <p className="text-xs font-medium text-muted-foreground mb-2">Características:</p>
              <ul className="text-xs space-y-1 text-muted-foreground">
                {plan.id === 'free' && (
                  <>
                    <li>• 3 kupones activos</li>
                    <li>• Analíticas básicas</li>
                    <li>• Perfil público</li>
                  </>
                )}
                {plan.id === 'starter' && (
                  <>
                    <li>• 3 kupones activos</li>
                    <li>• Soporte por email</li>
                    <li>• Listado prioritario</li>
                  </>
                )}
                {plan.id === 'pro' && (
                  <>
                    <li>• 15 kupones activos</li>
                    <li>• AI Marketing Tools</li>
                    <li>• Analíticas avanzadas</li>
                    <li>• Soporte prioritario</li>
                  </>
                )}
                {plan.id === 'business' && (
                  <>
                    <li>• Kupones ilimitados</li>
                    <li>• Todas las funciones Pro</li>
                    <li>• Soporte dedicado 24/7</li>
                    <li>• API personalizada</li>
                  </>
                )}
              </ul>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Refresh Button */}
      <div className="flex justify-center">
        <button
          onClick={() => { fetchPlans(); fetchStats(); }}
          className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-2 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Actualizar datos
        </button>
      </div>
    </div>
  );
}
